import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { getTabsForCopilotPage } from 'apps/sigma2-ui/src/lib/copilot';
import cn from 'classnames';
import { CopilotType } from 'libs/end-to-end/src/copilot';
import { CopilotOrder } from 'apps/sigma2-ui/src/lib/copilot';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { useSearchParams } from 'react-router-dom';
import { CopilotPageAnalyticsEventSource } from 'libs/end-to-end/src/analytics/CopilotPageAnalyticsEvent';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { useParams } from 'react-router';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { PromotionCopilotCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/PromotionCopilotDefinition';
import { useActiveFuse } from 'apps/sigma2-ui/src/lib/hooks/useActiveFuse';
import { PromotionCopilotView } from 'apps/sigma2-ui/src/copilot/components/PromotionCopilotView';
import { HiringCopilotCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/HiringCopilotDefinition';
import { HiringCopilotView } from 'apps/sigma2-ui/src/copilot/components/HiringCopilotView';
import { PayCorrectionView } from 'apps/sigma2-ui/src/pay-correction/components/PayCorrectionView';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import { PayBenchmarkingView } from 'apps/sigma2-ui/src/pay-benchmarking/components/PayBenchmarkingView';
import { isEntitledToHiringCopilot } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToPayBenchmarking } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToPayCorrection } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToPromotionCopilot } from 'libs/end-to-end/src/group-entitlements';
import { isHiringCopilotEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPayBenchmarkingEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPayCorrectionEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPromotionCopilotEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';

export const CopilotPage = () => {
  const language = useLanguage();
  const navigate = useNavigateSafe();
  const { myUser, userAuthenticatedApiCall } = useUser();
  const { myObservation } = useObservation();

  useAuthenticatedPageRedirect();
  const [searchParams] = useSearchParams();

  const actionParam = searchParams.get('action');

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  const hiringCopilotEnabled = useMemo(() => {
    return (
      isHiringCopilotEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToHiringCopilot({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const promotionCopilotEnabled = useMemo(() => {
    return (
      isPromotionCopilotEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToPromotionCopilot({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const payCorrectionEnabled = useMemo(() => {
    return (
      isPayCorrectionEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToPayCorrection({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const payBenchmarkingEnabled = useMemo(() => {
    return (
      isPayBenchmarkingEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToPayBenchmarking({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const copilotType = useCopilotType({
    hiringCopilotEnabled,
    promotionCopilotEnabled,
    payCorrectionEnabled,
    payBenchmarkingEnabled,
  });

  const values = getTabsForCopilotPage({
    hiringCopilotEnabled,
    promotionCopilotEnabled,
    payCorrectionEnabled,
    payBenchmarkingEnabled,
  });

  const organizationId = myUser?.organization?.id;

  return (
    <MainLayout showBackToTop={false}>
      <Helmet>
        <title>{documentTitle(language.copilot())}</title>
      </Helmet>

      <ObservationLoadingAndFailureIndicators
        isLoadingStill={isLoadingStill}
        failedToLoadObservation={failedToLoadObservation}
      />

      {!isLoadingStill && organizationId && myObservation && copilotType && (
        <>
          <CopilotPageSection>
            <div className="mb-2 2xl:mb-4">
              <S2Typography
                variant="headline-3"
                translatedContent={language.copilot()}
              />
            </div>

            <TabsRadioGroup
              currValue={copilotType as string}
              values={values}
              onChange={(value: string) => {
                navigate(`/copilot/${value}`);
              }}
              minWidth="auto"
              gap="md"
              underlineBgClass="bg-secondary"
              renderTab={(tab) => {
                const value = tab.value as {
                  displayedValue: string;
                  tabValue: string;
                  automationTag: string;
                };
                return (
                  <span className="w-min">
                    <span
                      className={cn('mx-1 font-sans text-lg font-semibold', {
                        'text-secondary': value.tabValue == copilotType,
                        'text-gray-600': value.tabValue != copilotType,
                      })}
                    >
                      {value.displayedValue}
                    </span>
                  </span>
                );
              }}
            />

            <CopilotViews
              copilotType={copilotType}
              organizationId={organizationId}
              myObservation={myObservation}
              userAuthenticatedApiCall={userAuthenticatedApiCall}
              actionParam={actionParam}
              calibrationFraming={myObservation.calibrationFraming}
            />
          </CopilotPageSection>
        </>
      )}
    </MainLayout>
  );
};

// eslint-disable-next-line complexity
const CopilotViews = ({
  copilotType,
  userAuthenticatedApiCall,
  myObservation,
  organizationId,
  actionParam,
  calibrationFraming,
}: {
  copilotType: CopilotType;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  myObservation: MyObservation;
  organizationId: string;
  actionParam: string | null;
  calibrationFraming: CalibrationFraming;
}) => {
  const analytics = useAnalytics();

  const shareLinkRawData = useShareLinkSearchParameter();

  useEffect(() => {
    if (!copilotType) {
      return;
    }

    const source: CopilotPageAnalyticsEventSource =
      actionParam !== null
        ? 'Upsell'
        : shareLinkRawData !== null
          ? 'Share Link'
          : 'Copilots Tab';
    if (copilotType === 'hiring') {
      analytics.trackEvent({
        eventName: 'Copilot Page Viewed',
        eventProperties: {
          selectedTab: 'Hiring',
          source,
        },
      });
    } else if (copilotType === 'promotion') {
      analytics.trackEvent({
        eventName: 'Copilot Page Viewed',
        eventProperties: {
          selectedTab: 'Promotion',
          source,
        },
      });
    } else if (copilotType === 'pay-benchmarking') {
      analytics.trackEvent({
        eventName: 'Copilot Page Viewed',
        eventProperties: {
          selectedTab: 'Pay Benchmarking',
          source,
        },
      });
    } else if (copilotType === 'pay-correction') {
      analytics.trackEvent({
        eventName: 'Copilot Page Viewed',
        eventProperties: {
          selectedTab: 'Pay Correction',
          source,
        },
      });
    } else {
      throw guardExhaustive(copilotType);
    }
  }, [actionParam, analytics, copilotType, shareLinkRawData]);

  return (
    <>
      <HiringCopilot
        active={copilotType === 'hiring'}
        userAuthenticatedApiCall={userAuthenticatedApiCall}
        myObservation={myObservation}
        defaultOrganizationId={organizationId}
      />

      <PromotionCopilot
        active={copilotType === 'promotion'}
        userAuthenticatedApiCall={userAuthenticatedApiCall}
        myObservation={myObservation}
        defaultOrganizationId={organizationId}
      />

      <PayCorrectionCopilot
        active={copilotType === 'pay-correction'}
        myObservation={myObservation}
        defaultOrganizationId={organizationId}
        calibrationFraming={calibrationFraming}
      />

      <PayBenchmarkingCopilot
        defaultObservation={myObservation.type}
        defaultOrganizationId={organizationId}
        active={copilotType === 'pay-benchmarking'}
      />
    </>
  );
};

const CopilotPageSection: React.FC<{
  children: React.ReactNode;
}> = (props) => {
  const { children } = props;
  return (
    <div className="flex flex-row">
      <div className="flex flex-row w-full max-w-[1600px] mx-auto">
        <div className="flex flex-row w-full relative">
          <main className="flex-1 flex flex-col p-4 2xl:p-5 3xl:p-6 pr-6 2xl:pr-7 3xl:pr-8 relative">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export const useCopilotType = ({
  hiringCopilotEnabled,
  promotionCopilotEnabled,
  payCorrectionEnabled,
  payBenchmarkingEnabled,
}: {
  hiringCopilotEnabled: boolean;
  promotionCopilotEnabled: boolean;
  payCorrectionEnabled: boolean;
  payBenchmarkingEnabled: boolean;
}) => {
  const { copilotType: copilotTypeRaw } = useParams<{ copilotType: string }>();

  return useMemo(() => {
    const parseResult = CopilotType.safeParse(copilotTypeRaw);

    const parsedCopilotType: CopilotType | null = parseResult.success
      ? parseResult.data
      : null;

    if (!parsedCopilotType) {
      for (const copilotType of CopilotOrder) {
        if (copilotType === 'hiring' && hiringCopilotEnabled) {
          return 'hiring';
        }

        if (copilotType === 'pay-benchmarking' && payBenchmarkingEnabled) {
          return 'pay-benchmarking';
        }

        if (copilotType === 'pay-correction' && payCorrectionEnabled) {
          return 'pay-correction';
        }

        if (copilotType === 'promotion' && promotionCopilotEnabled) {
          return 'promotion';
        }
      }

      return null;
    }

    if (parsedCopilotType === 'hiring' && !hiringCopilotEnabled) {
      return null;
    }

    if (parsedCopilotType === 'promotion' && !promotionCopilotEnabled) {
      return null;
    }

    if (parsedCopilotType === 'pay-correction' && !payCorrectionEnabled) {
      return null;
    }

    if (parsedCopilotType === 'pay-benchmarking' && !payBenchmarkingEnabled) {
      return null;
    }

    return parsedCopilotType;
  }, [
    copilotTypeRaw,
    hiringCopilotEnabled,
    payBenchmarkingEnabled,
    payCorrectionEnabled,
    promotionCopilotEnabled,
  ]);
};

export const PromotionCopilot = ({
  userAuthenticatedApiCall,
  defaultOrganizationId,
  myObservation,
  active,
}: {
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  active: boolean;
  myObservation: MyObservation;
  defaultOrganizationId: string;
}) => {
  const shouldShow = useActiveFuse(active);

  return (
    <div className={cn('pt-8', active ? 'block' : 'hidden')}>
      {shouldShow && (
        <CustomViz
          observation={myObservation.type}
          organizationId={defaultOrganizationId}
          userAuthenticatedApiCall={userAuthenticatedApiCall}
          customVizDefinition={PromotionCopilotCustomVizDefinition}
        >
          <PromotionCopilotView
            myObservation={myObservation}
            organizationId={defaultOrganizationId}
          />
        </CustomViz>
      )}
    </div>
  );
};

export const HiringCopilot = ({
  myObservation,
  defaultOrganizationId,
  userAuthenticatedApiCall,
  active,
}: {
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  myObservation: MyObservation;
  defaultOrganizationId: string;
  active: boolean;
}) => {
  const shouldShow = useActiveFuse(active);

  return (
    <div className={cn('pt-8', active ? 'block' : 'hidden')}>
      {shouldShow && (
        <CustomViz
          observation={myObservation.type}
          organizationId={defaultOrganizationId}
          userAuthenticatedApiCall={userAuthenticatedApiCall}
          customVizDefinition={HiringCopilotCustomVizDefinition}
        >
          <HiringCopilotView
            myObservation={myObservation}
            organizationId={defaultOrganizationId}
          />
        </CustomViz>
      )}
    </div>
  );
};

export const PayCorrectionCopilot = ({
  active,
  myObservation,
  defaultOrganizationId,
  calibrationFraming,
}: {
  active: boolean;
  myObservation: MyObservation;
  defaultOrganizationId: string;
  calibrationFraming: CalibrationFraming;
}) => {
  const shouldShow = useActiveFuse(active);

  return (
    <div className={cn('pt-8 px-6', active ? 'block' : 'hidden')}>
      {shouldShow && (
        <PayCorrectionView
          myObservation={myObservation}
          organizationId={defaultOrganizationId}
          calibrationFraming={calibrationFraming}
        />
      )}
    </div>
  );
};

export const PayBenchmarkingCopilot = ({
  active,
  defaultObservation,
  defaultOrganizationId,
}: {
  active: boolean;
  defaultObservation: ObservationLocationType;
  defaultOrganizationId: string;
}) => {
  const shouldShow = useActiveFuse(active);

  return (
    <div className={cn('pt-8', active ? 'block' : 'hidden')}>
      {shouldShow && (
        <PayBenchmarkingView
          observation={defaultObservation}
          organizationId={defaultOrganizationId}
        />
      )}
    </div>
  );
};
